import { FixedSizeList as List } from 'react-window';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

const ItemChip = ({ primary, secondary, isSelected, onClick }) => (
  <Chip
    label={
      <Stack direction="column" spacing={0.3} alignItems="flex-start">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Iconify icon="mdi:account" width={14} height={14} />
          <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{primary}</Box>
        </Stack>
        {secondary && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {secondary}
          </Typography>
        )}
      </Stack>
    }
    variant={isSelected ? 'filled' : 'outlined'}
    color={isSelected ? 'primary' : 'default'}
    onClick={onClick}
    sx={{
      width: '100%',
      justifyContent: 'flex-start',
      py: 0.5,
      minHeight: 48,
      '& .MuiChip-label': { width: '100%', display: 'block', px: 1, py: 0.5 },
      '&:hover': { bgcolor: isSelected ? 'primary.dark' : 'action.hover' },
    }}
  />
);

// ----------------------------------------------------------------------

function SelectionPanel({
  items,
  getItemId,
  getItemPrimary,
  getItemSecondary,
  getItemGroup,
  searchPlaceholder,
  selectedIds,
  onToggle,
  itemLabel = 'item',
}) {
  const [groupFilter, setGroupFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const groups = useMemo(() => {
    const set = new Set(items.map(getItemGroup).filter(Boolean));
    return Array.from(set).sort();
  }, [items, getItemGroup]);

  const filteredItems = useMemo(() => {
    let filtered = items;
    if (groupFilter !== 'all') filtered = filtered.filter((i) => getItemGroup(i) === groupFilter);
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (i) =>
          getItemPrimary(i).toLowerCase().includes(q) ||
          (getItemSecondary(i) || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [items, groupFilter, debouncedSearch, getItemGroup, getItemPrimary, getItemSecondary]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allFilteredSelected =
    filteredItems.length > 0 && filteredItems.every((i) => selectedSet.has(getItemId(i)));

  const handleSelectAll = useCallback(() => {
    const ids = filteredItems.map(getItemId);
    if (allFilteredSelected) {
      onToggle((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      onToggle((prev) => Array.from(new Set([...prev, ...ids])));
    }
  }, [filteredItems, allFilteredSelected, getItemId, onToggle]);

  return (
    <Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Chip
          label={`All (${items.length})`}
          variant={groupFilter === 'all' ? 'filled' : 'outlined'}
          color={groupFilter === 'all' ? 'primary' : 'default'}
          onClick={() => setGroupFilter('all')}
        />
        {groups.map((group) => {
          const count = items.filter((i) => getItemGroup(i) === group).length;
          const selectedCount = items.filter(
            (i) => getItemGroup(i) === group && selectedSet.has(getItemId(i))
          ).length;
          return (
            <Chip
              key={group}
              label={`${group} (${selectedCount}/${count})`}
              variant={groupFilter === group ? 'filled' : 'outlined'}
              color={groupFilter === group ? 'primary' : 'default'}
              onClick={() => setGroupFilter(group)}
              icon={<Iconify icon="mdi:account" />}
            />
          );
        })}
      </Stack>

      <TextField
        fullWidth
        size="small"
        placeholder={searchPlaceholder || 'Search...'}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 1 }}
      />

      <Stack
        sx={{ mt: 1, mb: 2 }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Button size="small" onClick={handleSelectAll}>
          {allFilteredSelected ? 'Deselect All' : 'Select All'} ({filteredItems.length})
        </Button>
        <Typography variant="caption" color="text.secondary">
          {selectedIds.length} {itemLabel}(s) selected{' '}
          {groupFilter !== 'all' || debouncedSearch ? `from ${filteredItems.length} filtered` : ''}
        </Typography>
      </Stack>

      <Box sx={{ height: 400 }}>
        <List height={400} itemCount={filteredItems.length} itemSize={60} width="100%">
          {({ index, style }) => {
            const item = filteredItems[index];
            const id = getItemId(item);
            return (
              <Box style={style} key={id} sx={{ px: 1, py: 0.5 }}>
                <ItemChip
                  primary={getItemPrimary(item)}
                  secondary={getItemSecondary(item)}
                  isSelected={selectedSet.has(id)}
                  onClick={() =>
                    onToggle((prev) =>
                      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
                    )
                  }
                />
              </Box>
            );
          }}
        </List>
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

export const NotificationUserSelector = ({ users = [], selectedUsers, setSelectedUsers }) => (
  <SelectionPanel
    items={users}
    getItemId={(u) => u.Id}
    getItemPrimary={(u) =>
      `${u.FirstName || ''} ${u.LastName || ''}`.trim() || u.Email || 'Unknown'
    }
    getItemSecondary={(u) => u.Email}
    getItemGroup={(u) => u.Role?.Name || 'Unknown'}
    searchPlaceholder="Search by name or email..."
    selectedIds={selectedUsers}
    onToggle={setSelectedUsers}
    itemLabel="user"
  />
);

// ----------------------------------------------------------------------

export const NotificationParticipantSelector = ({
  participants = [],
  selectedUsers,
  setSelectedUsers,
}) => (
  <SelectionPanel
    items={participants}
    getItemId={(p) => p.UserId}
    getItemPrimary={(p) =>
      `${p.FirstName || ''} ${p.LastName || ''} - ${p.Role.Name || ''}`.trim() ||
      p.Email ||
      'Unknown'
    }
    getItemSecondary={(p) => p.Email}
    getItemGroup={(p) => {
      const project = p.Projects?.[0];
      return project?.ProjectParticipationType?.Name || 'Unknown';
    }}
    searchPlaceholder="Search by name or email..."
    selectedIds={selectedUsers}
    onToggle={setSelectedUsers}
    itemLabel="participant"
  />
);

// ----------------------------------------------------------------------

export const NotificationTargetSelector = ({
  users = [],
  participants = [],
  selectedUsers,
  setSelectedUsers,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<Iconify icon="mdi:account-group" width={20} />}
          iconPosition="start"
          label={`Users (${users.length})`}
        />
        <Tab
          icon={<Iconify icon="mdi:account-school" width={20} />}
          iconPosition="start"
          label={`Participants (${participants.length})`}
        />
      </Tabs>

      {activeTab === 0 && (
        <NotificationUserSelector
          users={users}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
        />
      )}

      {activeTab === 1 && (
        <NotificationParticipantSelector
          participants={participants}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
        />
      )}
    </Box>
  );
};
